import { useNavigate } from 'react-router';
import { LogOut, Settings as SettingsIcon } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import {
  filterSettingsMenuForPermissions,
  SETTINGS_MENU_DESTINATIONS,
} from '@/navigation/registry';

export function UserMenu() {
  const navigate = useNavigate();
  const auth = useAuth();
  const administrationLinks = filterSettingsMenuForPermissions(
    SETTINGS_MENU_DESTINATIONS,
    auth.permissions,
  ).filter((destination) => destination.id !== 'settings');
  const initials =
    auth.user?.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'U';

  const signOut = async () => {
    await auth.signOut();
    toast.success('Signed out');
    navigate('/auth', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="backdrop-blur-glass focus-visible:ring-ring flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-left shadow-inner transition-colors hover:border-white/20 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2"
        >
          <Avatar className="h-7 w-7">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="hidden leading-tight sm:block">
            <p className="text-foreground text-xs font-medium">{auth.user?.name ?? 'User'}</p>
            <p className="text-2xs text-muted-foreground">{auth.tenant?.name ?? 'Tenant'}</p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel>Session</DropdownMenuLabel>
        <div className="text-muted-foreground space-y-1 px-2.5 pb-2 text-xs">
          <p className="text-foreground font-medium">{auth.user?.email}</p>
          <p>{auth.roles.length > 0 ? auth.roles.join(', ') : 'No role assigned'}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <SettingsIcon className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          Settings
        </DropdownMenuItem>
        {administrationLinks.map((destination) => {
          const Icon = destination.icon;
          return (
            <DropdownMenuItem key={destination.id} onSelect={() => navigate(destination.path)}>
              <Icon className="text-muted-foreground h-4 w-4" aria-hidden="true" />
              {destination.label}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void signOut();
          }}
        >
          <LogOut className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
